import { z } from 'zod';
// Shared revenue-analytics contract. A revenue event is a normal Logspot event
// with its monetary `value` populated (major units, e.g. dollars) plus a
// `currency` (ISO-4217) and optional `external_id` (source transaction id). The
// reserved event name (default 'Payment', project-configurable) marks an event
// as revenue so it is identifiable/filterable.
// ── Default revenue event name ────────────────────────────
// A label the SDK's `revenue()` helper emits; revenue is recognized by value + currency.
export const DEFAULT_REVENUE_EVENT_NAME = 'Payment';
// ── Currency ──────────────────────────────────────────────
// ISO-4217 alphabetic code, exactly three uppercase letters. We store the code
// verbatim with the amount; V1 does NO FX conversion. Project `baseCurrency`
// (default 'USD') is the display currency.
export const DEFAULT_BASE_CURRENCY = 'USD';
const ISO_4217_REGEX = /^[A-Z]{3}$/;
/**
 * Normalize a client-supplied currency to the canonical ISO-4217 shape
 * (uppercase, trimmed). Returns null when the input is absent or not a valid
 * 3-letter code — callers decide whether that is an error or a fallback to the
 * project base currency.
 */
export const normalizeCurrency = (input) => {
    if (typeof input !== 'string')
        return null;
    const code = input.trim().toUpperCase();
    return ISO_4217_REGEX.test(code) ? code : null;
};
export const currencyCodeSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => ISO_4217_REGEX.test(value), {
    message: 'currency must be a 3-letter ISO-4217 code (e.g. USD)',
});
// ── Revenue input (server-validated) ──────────────────────
// The generic revenue path accepts `value` + `currency` on the event pipeline.
// `value` is in MAJOR units and must be >= 0 on the generic/positive path —
// refunds use a dedicated negative path (the server owns that distinction), so
// the public DTO never accepts a negative amount directly. The server is the
// source of truth for this validation; the SDK is a thin wrapper.
export const revenueInputSchema = z.object({
    value: z
        .number()
        .refine((value) => Number.isFinite(value), {
        message: 'value must be a finite number',
    })
        .refine((value) => value >= 0, { message: 'value must be >= 0' }),
    currency: currencyCodeSchema.optional(),
    external_id: z.string().max(255).optional(),
});
// ── Reserved revenue metadata keys ────────────────────────
// Product/plan descriptors carried in event metadata for breakdowns. These are
// reserved keys the Stripe Connect path and `revenue()` helper populate.
export const REVENUE_METADATA_KEYS = {
    product: 'product',
    plan: 'plan',
    interval: 'interval',
    quantity: 'quantity',
};
// One-time vs recurring revenue.
export const REVENUE_INTERVALS = ['one_time', 'recurring'];
// ── Attribution model ─────────────────────────────────────
// Reports can be broken down by the source that ACQUIRED the entity
// (first-touch, durable) or the entity's MOST-RECENT source (last-touch). V1 is
// first + last only; multi-touch is deferred.
export const REVENUE_ATTRIBUTION_MODELS = [
    'first_touch',
    'last_touch',
];
// Reserved metadata key prefixes for the flattened attribution snapshot carried
// in event metadata (per the PRD appendix). Phase 2 writes/reads these; defined
// here so the writer and report queries never drift.
export const ATTRIBUTION_METADATA_PREFIXES = {
    identityFirstTouch: 'ft_',
    identityLastTouch: 'lt_',
    accountFirstTouch: 'acct_ft_',
    accountLastTouch: 'acct_lt_',
};
// Reserved internal metadata keys the platform stamps onto events: revenue
// plumbing (interval/quantity), the refund reference, the Stripe PaymentIntent
// passthrough, and the converting-session id. These are infrastructure, not
// user-defined properties, so they're hidden from BOTH the queries-page property
// picker and the event metadata view. NOTE: `product`/`plan` are intentionally
// NOT reserved — they're meaningful business dimensions (revenueByProperty
// surfaces them), so they stay visible.
export const RESERVED_METADATA_KEYS = new Set([
    '_refund_of',
    '_stripe_pi',
    'logspot_session_id',
    'interval',
    'quantity',
]);
// Reserved key PREFIXES — the flattened first/last-touch attribution snapshot
// (ft_/lt_/acct_ft_/acct_lt_) written onto revenue events. Hidden alongside the
// keys above.
export const RESERVED_METADATA_PREFIXES = [
    ATTRIBUTION_METADATA_PREFIXES.identityFirstTouch,
    ATTRIBUTION_METADATA_PREFIXES.identityLastTouch,
    ATTRIBUTION_METADATA_PREFIXES.accountFirstTouch,
    ATTRIBUTION_METADATA_PREFIXES.accountLastTouch,
];
// True when a metadata key is a user-facing property (safe to show in the
// property picker and event views), false for the platform's internal plumbing.
// Single source of truth shared by the backend (property picker + ingest) and
// the admin event feed so the two never drift.
export const isUserFacingMetadataKey = (key) => !RESERVED_METADATA_KEYS.has(key) &&
    !RESERVED_METADATA_PREFIXES.some((prefix) => key.startsWith(prefix));
// Remove the reserved/internal keys from client metadata before merging it with
// the server's data. Otherwise a caller with only the public key could set
// those keys directly to forge attribution or tamper with refund matching.
export const stripReservedMetadata = (metadata) => {
    if (!metadata)
        return undefined;
    const cleaned = {};
    for (const [key, value] of Object.entries(metadata)) {
        if (isUserFacingMetadataKey(key))
            cleaned[key] = value;
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};
//# sourceMappingURL=revenue.js.map